"use client";

import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import styles from "./SearchResults.module.scss";

// Utils
import search from "../../utils/search";

// Helpers
import DOMPurify from "@/app/[lang]/utils/dompurify";
import { useSearchParams } from "next/navigation";

// Components
import SearchBar from "../SearchBar";
import SearchFAQs from "../SearchFAQs";
import SearchArticles from "../SearchArticles";
import SearchSolutions from "../SearchSolutions";

import { Divider, Flex, Text } from "@/app/[lang]/components/ui/AntdLite";
import { useSearchStore } from "../../utils/useSearchStore";



interface IResult {
  text: string;
  value: number;
  onClick: () => void;
  active?: boolean;
}

interface SearchResultElement {
  data: any[];
  meta: any;
}

interface ISearchResults {
  total: number;
  articles: SearchResultElement;
  pages: SearchResultElement;
  faqs: SearchResultElement;
}

const Result = ({ text, value, onClick, active = false }: IResult) => {
  const itemClassNames = classNames(
    styles.resultMenuItem,
    active && styles.active
  );
  const textClassNames = classNames(styles.text, active && styles.active);
  const labelClassNames = classNames(styles.label, active && styles.active);

  return (
    <Flex className={itemClassNames} align="center" gap="8px" onClick={onClick}>
      <Text className={textClassNames}>{text}</Text>
      <Text className={labelClassNames}>{value}</Text>
    </Flex>
  );
};

const SearchResults = () => {
  const searchParams = useSearchParams();

  const [parsedResults, setParsedResults] = useState<ISearchResults>();
  const [selected, setSelected] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const searchData = useSearchStore((state) => state.searchData);
  const isLoading = useSearchStore((state) => state.isLoading);
  const fetchSearchData = useSearchStore((state) => state.fetchSearchData);

  useEffect(() => {
    if (!searchData && !isLoading) {
      fetchSearchData();
    }
  }, [searchData, isLoading, fetchSearchData]);

  useEffect(() => {
    const query = searchParams.get("query");

    if (query) {
      search(query, "all", currentPage, 1)
        .then((res) => {
          return res.json();
        })
        .then((data) => {
          setParsedResults({
            total:
              (data.articles?.meta?.pagination?.total || 0) +
              (data.pages?.meta?.pagination?.total || 0) +
              (data.faqs?.meta?.pagination?.total || 0),
            ...data,
          });
        });
    }
  }, [searchParams, selected, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selected, searchParams]);

  const innerHtml = useMemo(() => {
    return searchData?.data.attributes.resultsText
      .replace("{count}", `<b>${parsedResults?.total ?? 0}</b>`)
      .replace("{query}", `<b>${searchParams.get("query")}</b>`);
  }, [searchData, parsedResults, searchParams]);

  if ( ! searchData ) {
    return null;
  }

  return (
    <section className={styles.searchResultsWrapper}>
      <>
        <div className={styles.searchbarWrapper}>
          <SearchBar />
        </div>

        {(parsedResults?.total ?? 0) === 0 && (
          <Flex className={styles.noResults}>
            <Text>{searchData?.data.attributes.noResultsText}</Text>
          </Flex>
        )}

        {(parsedResults?.total ?? 0) > 0 && (
          <>
            <div className="container">
              { innerHtml && <span
                className={styles.title}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(innerHtml),
                }}
              /> }

              <Divider className={styles.divider} />

              <div className={styles.overflow}>
                <Flex className={styles.buttons}>
                  <Result
                    text={searchData.data.attributes.total}
                    value={parsedResults?.total ?? 0}
                    active={selected === 0}
                    onClick={() => setSelected(0)}
                  />

                  <Result
                    text={searchData.data.attributes.articles}
                    value={parsedResults?.articles?.meta?.pagination.total ?? 0}
                    active={selected === 1}
                    onClick={() => setSelected(1)}
                  />

                  <Result
                    text={searchData.data.attributes.solutions}
                    value={parsedResults?.pages?.meta?.pagination.total ?? 0}
                    active={selected === 2}
                    onClick={() => setSelected(2)}
                  />

                  <Result
                    text={searchData.data.attributes.faqs}
                    value={parsedResults?.faqs?.meta?.pagination.total ?? 0}
                    active={selected === 3}
                    onClick={() => setSelected(3)}
                  />
                </Flex>
              </div>
            </div>

            {Boolean(
              parsedResults?.pages?.data.length &&
                (selected === 0 || selected === 2)
            ) && (
              <SearchSolutions
                title={searchData.data.attributes.solutions}
                buttonText={searchData.data.attributes.moreResultsText}
                onClick={selected === 0 ? () => setSelected(2) : undefined}
              />
            )}

            {Boolean(
              parsedResults?.articles?.data.length &&
                (selected === 0 || selected === 1)
            ) && (
              <SearchArticles
                title={searchData.data.attributes.articles}
                buttonText={searchData.data.attributes.moreResultsText}
                onClick={selected === 0 ? () => setSelected(1) : undefined}
              />
            )}

            {Boolean(
              parsedResults?.faqs?.data.length &&
                (selected === 0 || selected === 3)
            ) && (
              <SearchFAQs
                title={searchData.data.attributes.faqs}
                buttonText={searchData.data.attributes.moreResultsText}
                onClick={selected === 0 ? () => setSelected(3) : undefined}
              />
            )}
          </>
        )}
      </>
    </section>
  );
};

export default SearchResults;
